/**
 * Study Deck API client.
 * Thin wrapper around the shared axios `api` instance so JWT auth,
 * base URL, and response parsing are consistent across the three
 * study surfaces (Deck Hub, Session, Duel).
 */
import { api } from './apiClient';

// ── Hub / decks ────────────────────────────────────────────────────
export const getStudyHub = () =>
  api.get('/api/study/hub').then((r) => r.data);
export const getDeck = (deckId) =>
  api.get(`/api/study/decks/${deckId}`).then((r) => r.data);
export const createDeck = (payload) =>
  api.post('/api/study/decks', payload).then((r) => r.data);
export const createCard = (deckId, payload) =>
  api.post(`/api/study/decks/${deckId}/cards`, payload).then((r) => r.data);

// ── Session ────────────────────────────────────────────────────────
export const getNextCard = (deckId, focus) =>
  api.get('/api/study/session/next', {
    params: {
      ...(deckId ? { deck_id: deckId } : {}),
      ...(focus ? { focus } : {}),
    },
  })
     .then((r) => r.data);
export const revealCard = (cardId) =>
  api.get(`/api/study/session/reveal/${cardId}`).then((r) => r.data);
export const submitReview = (cardId, rating) =>
  api.post('/api/study/session/review', { card_id: cardId, rating })
     .then((r) => r.data);

// ── Duel ───────────────────────────────────────────────────────────
export const startDuel = (deckId, bestOf = 10) =>
  api.post('/api/study/duels', { deck_id: deckId, opponent: 'ai', best_of: bestOf })
     .then((r) => r.data);
export const invitePeerDuel = (deckId, partnerEmail, bestOf = 10) =>
  api.post('/api/study/duels', {
    deck_id: deckId, opponent: 'peer',
    partner_email: partnerEmail, best_of: bestOf,
  }).then((r) => r.data);
export const getDuel = (duelId) =>
  api.get(`/api/study/duels/${duelId}`).then((r) => r.data);
export const submitDuelAnswer = (duelId, cardId, correct, elapsedMs) =>
  api.post(`/api/study/duels/${duelId}/answer`, {
    card_id: cardId, correct, elapsed_ms: elapsedMs,
  }).then((r) => r.data);
export const getDuelInbox = () =>
  api.get('/api/study/duels/inbox').then((r) => r.data);
export const acceptDuel = (duelId) =>
  api.post(`/api/study/duels/${duelId}/accept`).then((r) => r.data);
export const declineDuel = (duelId) =>
  api.post(`/api/study/duels/${duelId}/decline`).then((r) => r.data);
export const cancelDuel = (duelId) =>
  api.post(`/api/study/duels/${duelId}/cancel`).then((r) => r.data);
export const searchBuddies = (q = '') =>
  api.get('/api/study/buddies/search', { params: { q } }).then((r) => r.data);

// ── Insights ───────────────────────────────────────────────────────
export const getMyInsights = () =>
  api.get('/api/insights/student/me').then((r) => r.data);
export const getMyInsightsMini = () =>
  api.get('/api/insights/student/me/mini').then((r) => r.data);
export const getFacultyTopicHealth = () =>
  api.get('/api/insights/faculty/topic-health').then((r) => r.data);
export const getFacultyStudents = (threshold = 60) =>
  api.get('/api/insights/faculty/students', { params: { threshold } }).then((r) => r.data);
export const getExecutiveRecallSummary = () =>
  api.get('/api/insights/executive/recall-summary').then((r) => r.data);
export const nudgeStudent = (payload) =>
  api.post('/api/insights/faculty/nudge', payload).then((r) => r.data);
export const getNudgeHistory = () =>
  api.get('/api/insights/faculty/nudges').then((r) => r.data);
export const getConceptMap = () =>
  api.get('/api/study/concept-map').then((r) => r.data);
